// Ionic & Angular Imports
import {Component} from '@angular/core';
import {Events, NavParams, LoadingController, Loading, ModalController, AlertController, Refresher} from 'ionic-angular';

// Service Imports 
import {rsReportSvc, ReportState} from '../../../../services/report.service';
import {rsInspectionSvc, Inspection} from '../../../../services/inspection.service';
import {InspectionType} from '../../../../services/inspectiontype.service';
import {JurisdictionType} from '../../../../services/jurisdictiontype.service';

// Component Imports
import {BasePage} from '../../../base.component';
import {InspectionModal} from './inspection-modal/inspection-modal.component';

@Component({
    templateUrl: 'inspection.html',
    providers: [
        rsInspectionSvc
    ]
})
export class InspectionPage extends BasePage
{
    // Class variables 
    private contentList : Inspection[];
    private inspectionList : InspectionType[];
    private jurisdictionList : JurisdictionType[];

    // Class Constructor
    constructor( 
        protected alertCtrl : AlertController, 
        private loadingCtrl : LoadingController,  
        private navParams : NavParams, 
        private eventSystem : Events,
        private modalCtrl : ModalController,
        private reportSvc: rsReportSvc, 
        private inspectionSvc : rsInspectionSvc
    ){
        // Construct base class
        super(alertCtrl);

        interface NavData {
            inspectionTypes: InspectionType[];
            jurisdictionTypes: JurisdictionType[]
            inspections : Inspection[];
        };

        // Retrieve the activity list from our NavParams       
        let navData : NavData = <NavData>this.navParams.get('content');

        console.log(navData);

        this.contentList = navData.inspections;
        this.inspectionList = navData.inspectionTypes;
        this.jurisdictionList = navData.jurisdictionTypes;
        
        this.sortContentList();
        this.sortInspectionTypes(); this.sortJurisdictionTypes();

        // Register our event handlers 
        this.registerEventHandlers( eventSystem );
    }

    // Class method used to subscribe to all events this class is interested in
    private registerEventHandlers( eventSystem : Events ) {
        eventSystem.subscribe( 'inspection:deleted', (data) => { this.deleteInspection( data ); });
        eventSystem.subscribe( 'inspection:updated', (data) => { this.updateInspection( data ); });
        eventSystem.subscribe( 'inspection:added',   (data) => { this.addInspection( data ); });
    }

   // Sorting method for ensuring our ContentList is always sorted by id (ie., oldest to newest entries)
    private sortContentList(reverse : boolean = false) {
        this.contentList.sort( (a,b) => {
            return reverse ? b.id - a.id : a.id - b.id;
        });
    }
    private sortInspectionTypes(reverse : boolean = false ) {
        this.inspectionList.sort( (a, b) => {
            let nameA : string = (a.name).toLowerCase();
            let nameB : string = (b.name).toLowerCase();
            if( nameA < nameB ) return -1;
            if( nameA > nameB ) return 1;
            return 0;
        });
    }
    private sortJurisdictionTypes(reverse : boolean = false ) {
        this.jurisdictionList.sort( (a, b) => {
            let nameA : string = (a.name).toLowerCase();
            let nameB : string = (b.name).toLowerCase();
            if( nameA < nameB ) return -1;
            if( nameA > nameB ) return 1;
            return 0;
        });
    }    

    // Class method used to add a new labor to our ContentList 
    private addInspection( inspection : Inspection ) : boolean {
        // Add this labor to the end of our ContentList  
        let length : number = this.contentList.push(inspection);

        // Finally, publish an event that our report has been updated
        this.eventSystem.publish( 'report:updated', ReportState.Inspections, length );      
        
        // Return successful
        return true;  
    }

    // Class method used to update an labor in our ContentList
    private updateInspection( inspection : Inspection ) : boolean {
        // Loop through each item in the ContentList to attempt to find a matching labor
        for( var idx = 0; idx < this.contentList.length; idx++ ) {
            let item : Inspection = this.contentList[idx];
            if( item.id === inspection.id ) {
                // Update the content of this labor within the ContentList
                this.contentList[idx] = inspection;
                return true;          
            }
        }
        // No match was found, return false 
        return false;
    }

    // Class method used to delete an labor in our ContentList
    private deleteInspection( inspection : Inspection ) : boolean {
        // Loop through each item in the ContentList to attempt to find a matching labor
        for( var idx = 0; idx < this.contentList.length; idx++ ) {
            let item : Inspection = this.contentList[idx];
            if( item.id === inspection.id ) {
                // Remove this labor from the ContentList
                this.contentList.splice( idx, 1 );

                // Publish an event that will update our report state 
                this.eventSystem.publish( 'report:updated', ReportState.Inspections, this.contentList.length );

                // Return successful
                return true;          
            }
        }
        // No match was found, return false 
        return false;
    }

    // Class method used by the template to edit an existing activity
    public updateContent( inspection : Inspection ) {
        // Create our ModalInstance and configure for editing
        this.modalCtrl.create( InspectionModal, { 
            inspection: inspection,
            inspectionTypes: this.inspectionList,
            jurisdictionTypes: this.jurisdictionList,
            editmode: true
        }).present();
    }

    // Class method used by the template to create a new activity
    public createContent( ) {
        // Create our ModalInstance and configure for creating 
        this.modalCtrl.create( InspectionModal, { 
            inspection: null,
            inspectionTypes: this.inspectionList,
            jurisdictionTypes: this.jurisdictionList,
            editmode: false
        }).present();      
    }

    // Class method used by the template to delete a subcontractor
    public deleteContent( inspection : Inspection ) {
        // Verify that we have a valid subcontractor id before trying to delete 
        if( inspection.id > 0 ) 
        {
            // Labor id is valid; instantiate delete confirmation
            this.displayConfirmation( 'Delete Inspection', 'Are you sure you want to delete "' + inspection.inspection.name + '" from this report?', () => 
            {
                // User confirmed- so let's initiate the delete process
                let loader : Loading = this.loadingCtrl.create( {
                    content: 'Deleting...'
                });
                loader.present().then( () => {
                    // Data hNow let's start our delete process 
                    this.inspectionSvc.deleteInspection( inspection.id, this.reportSvc.getActiveReportId() ).subscribe( result => {
                        // Data has been received from our service 
                        this.eventSystem.publish('inspection:deleted', inspection );
                    }, err => {
                        // Operation failed; dismiss loader and shown an error message 
                        loader.dismiss().then( () => {
                            this.displayError("There was an error deleting this inspection.  Please try again later.");
                        });
                    }, () => {
                        // Operation succeeded, dismiss loader
                        loader.dismiss();
                    });
                });
            });
        } else {
            // The subcontractor id supplied is not valid; display a generic error 
            this.displayError( 'An error has occurred.  Please try again later.' );
        }
    }

    // Class method used by the template to refresh the ContentList
    public refreshContent( refresher : Refresher ) {
        // Spawn a spinner while the ContentList is being refreshed
        let loader : Loading = this.loadingCtrl.create( {
            content: 'Refreshing...'
        });
        loader.present().then( () => {
            // The spinner is loaded, so let's get our refreshed ContentList
            this.inspectionSvc.getInspections( this.reportSvc.getActiveReportId() )
            .subscribe( result => {
                // Data was received from our service 
                this.contentList = result;
                this.sortContentList();

                // Publish an event that will update our report state 
                this.eventSystem.publish( 'report:updated', ReportState.Inspections, this.contentList.length );
            }, err => {
                // The operation failed, so let's handle this error 
                loader.dismiss().then( () => {
                    this.displayError( "There was an error refreshing the list.  Please try again later." ).then( () => {
                        refresher.complete();
                    });
                });
            }, () => {
                // The operation succeeded, let's dismiss our spinner and complete our refresh operation 
                loader.dismiss().then( () => {
                    refresher.complete();
                });
            });
        });
    }    
}


// WEBPACK FOOTER //
// ./src/pages/report/tab-main/inspection/inspection.component.ts